import { Button } from '@shared/components/common/Button';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
// import SuccessIcon from '@shared/assets/images/success_icon.svg';
import { ROUTES } from '@shared/configs/constants';
import { observer } from 'mobx-react-lite';
import { ArrowLeft, CheckCircle2, LoaderIcon } from 'lucide-react';
import { useStore } from '@/shared/hooks/useStore';
import { flowResult } from 'mobx';

export default observer(function VerifyEmailChangePage() {
    const {
        authStore: { verifyChangeEmail }
    } = useStore();
    // hook
    const [params] = useSearchParams();
    const token = params?.get('token') || null;

    // state
    const [isTokenValid, setIsTokenValid] = useState<boolean>(false);
    const [isVerifying, setIsVerifying] = useState<boolean>(true);
    const [isVerified, setIsVerified] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    // lifecycle
    useEffect(() => {
        onCheckToken();
    }, []);

    // function
    const onCheckToken = async () => {
        if (!token) {
            setIsVerifying(false);
            return;
        }

        try {
            setIsVerifying(true);
            const res = await flowResult(verifyChangeEmail({ token }));

            if (res) {
                setIsTokenValid(true);
                setIsVerified(true);
            } else {
                setErrorMessage('トークンが無効です。');
            }
        } catch (error) {
            setErrorMessage('メールアドレスの変更確認中にエラーが発生しました。');
        } finally {
            setIsVerifying(false);
        }
    };

    // Show loading state
    if (isVerifying) {
        return (
            <div className='sm:bg-background-2 bg-white my-10 flex justify-center items-center lg:py-7.5 md:py-6'>
                <div className='mx-auto flex w-full flex-col justify-center sm:w-[640px] rounded-2xl bg-[#F1F1F1] px-6 md:px-8 md:gap-8 gap-6 py-10'>
                    <h3 className='text-center page-title-hero'>メール変更確認</h3>
                    <div className='flex flex-col items-center justify-center py-8 gap-4'>
                        <LoaderIcon className='animate-spin size-12 text-primary' />
                        <p className='text-center text-base'>確認中...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Show error or not found if no token or invalid
    if (!token || (!isTokenValid && !isVerifying)) {
        return (
            <div className='sm:bg-background-2 bg-white my-10 flex justify-center items-center lg:py-7 md:py-6'>
                <div className='mx-auto flex w-full flex-col justify-center sm:w-[640px] rounded-2xl bg-[#F1F1F1] px-6 md:px-8 md:gap-8 gap-6 py-10'>
                    <h3 className='text-center page-title-hero'>メール変更確認</h3>
                    <div className='flex flex-col gap-8 w-full'>
                        <div className='md:p-4 sm:p-2 p-1 flex flex-col items-center md:gap-6 gap-4 rounded text-base font-medium w-full'>
                            <div className='text-center text-danger'>
                                {errorMessage || '無効なリンクです。'}
                            </div>
                            <div className='text-center text-sm text-text-secondary'>
                                リンクが無効または期限切れの可能性があります。
                                <br />
                                再度メールアドレスの変更手続きを行ってください。
                            </div>
                        </div>

                        <Button
                            asChild
                            type='button'
                            variant='default'
                            className='w-full rounded-xl h-14 text-sm md:text-lg font-bold'
                        >
                            <Link to={ROUTES.home.href}>ホームに戻る</Link>
                        </Button>

                        <p className='text-center'>
                            <Link to={ROUTES.profileEdit.href} className='inline-flex text-base items-center gap-1.5 font-medium group'>
                                <ArrowLeft size={24} color='#343330' strokeWidth={1.25} className='transition-transform duration-500 group-hover:-translate-x-1' />
                                プロフィール編集に戻る
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Show success page
    return (
        <div className='sm:bg-background-2 bg-white my-10 flex justify-center items-center lg:py-7 md:py-6'>
            <div className='mx-auto flex w-full flex-col justify-center sm:w-[640px] rounded-2xl bg-[#F1F1F1] px-6 md:px-8 md:gap-8 gap-6 py-10'>
                <h3 className='text-center page-title-hero'>メール変更確認</h3>

                {isVerified ?
                    (
                        <div className='flex flex-col gap-8 w-full'>
                            <div className='md:p-4 sm:p-2 p-1 flex flex-col items-center gap-4 rounded text-base font-medium w-full'>
                                {/* <img className='h-[100px]' src={SuccessIcon} alt='success-icon' /> */}
                                <div className='w-20 h-20 bg-green-200 rounded-full flex items-center justify-center mx-auto mb-4'>
                                    <CheckCircle2 className='size-10 text-green-600' />
                                </div>
                                <div className='text-center'>
                                    メールアドレスの変更が完了しました。
                                </div>
                                <div className='text-center text-sm text-text-secondary'>
                                    新しいメールアドレスでログインできます。
                                </div>
                            </div>

                            <Button
                                asChild
                                type='button'
                                variant='default'
                                className='w-full rounded-xl h-14 text-sm md:text-lg font-bold'
                            >
                                <Link to={ROUTES.home.href}>ログイン</Link>
                            </Button>

                            <p className='text-center'>
                                <Link to={ROUTES.profile.href} className='inline-flex text-base items-center gap-1.5 font-medium group'>
                                    <ArrowLeft size={24} color='#343330' strokeWidth={1.25} className='transition-transform duration-500 group-hover:-translate-x-1' />
                                    プロフィールに戻る
                                </Link>
                            </p>
                        </div>
                    ) :
                    (
                        <div className='flex flex-col gap-8 w-full'>
                            <div className='md:p-4 sm:p-2 p-1 flex flex-col items-center md:gap-6 gap-4 rounded text-base font-medium w-full'>
                                <div className='text-center text-danger'>
                                    メールアドレスの変更に失敗しました。
                                </div>
                            </div>

                            <Button
                                asChild
                                type='button'
                                variant='default'
                                className='w-full rounded-xl h-14 text-sm md:text-lg font-bold'
                            >
                                <Link to={ROUTES.profileEdit.href}>プロフィール編集に戻る</Link>
                            </Button>
                        </div>
                    )}
            </div>
        </div>
    );
});
